import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type RefObject } from 'react';
import type { TableColumn } from './types';

type DropTarget = { id: string; side: 'before' | 'after' };
type Drag = {
  id: string;
  ids: string[];
  element: HTMLButtonElement;
  pointerId: number;
  startX: number;
  startY: number;
  x: number;
  y: number;
};

export function orderColumns(columns: TableColumn[], order: string[]): TableColumn[] {
  const remaining = new Map(columns.map((column) => [column.id, column]));
  const ordered: TableColumn[] = [];
  order.forEach((id) => {
    const column = remaining.get(id);
    if (column) {
      ordered.push(column);
      remaining.delete(id);
    }
  });
  return [...ordered, ...remaining.values()];
}

function headerAtPointer(scroller: HTMLDivElement, drag: Drag) {
  const viewport = scroller.getBoundingClientRect();
  const headers = [...scroller.querySelectorAll<HTMLTableCellElement>('thead th[data-column-id]')];
  const headerRect = headers[0]?.getBoundingClientRect();
  const inside =
    headerRect &&
    drag.x >= viewport.left &&
    drag.x <= viewport.right &&
    drag.y >= headerRect.top - 12 &&
    drag.y <= headerRect.bottom + 12;
  if (!inside) return { target: null, scrollDelta: 0 };
  const header = headers.find((element) => {
    const rect = element.getBoundingClientRect();
    return drag.x >= rect.left && drag.x <= rect.right;
  });
  const rect = header?.getBoundingClientRect();
  const target: DropTarget | null =
    header && rect
      ? { id: header.dataset.columnId!, side: drag.x < rect.left + rect.width / 2 ? 'before' : 'after' }
      : null;
  const edge = Math.min(40, viewport.width / 3);
  const scrollDelta = drag.x < viewport.left + edge ? -12 : drag.x > viewport.right - edge ? 12 : 0;
  return { target, scrollDelta };
}

const moved = (drag: Drag) => Math.hypot(drag.x - drag.startX, drag.y - drag.startY) >= 5;

/** Pointer-only reordering, with separate handles from header sorting/resizing.
 * Preview an insertion edge and commit once on drop, rather than reflowing cells during drag.
 */
export function useColumnOrdering(
  columns: TableColumn[],
  enabled: boolean,
  scrollerRef: RefObject<HTMLDivElement>,
  onOrderChange: (ids: string[]) => void,
) {
  const dragRef = useRef<Drag | null>(null);
  const frameRef = useRef<number>();
  const commitRef = useRef(onOrderChange);
  commitRef.current = onOrderChange;
  const [preview, setPreview] = useState<{ id: string; target: DropTarget | null } | null>(null);
  const idsKey = JSON.stringify(columns.map((column) => column.id));

  const cleanup = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
    frameRef.current = undefined;
    if (drag?.element.hasPointerCapture(drag.pointerId)) drag.element.releasePointerCapture(drag.pointerId);
  }, []);
  const cancel = useCallback(() => {
    cleanup();
    setPreview(null);
  }, [cleanup]);
  useEffect(() => {
    cancel();
  }, [idsKey, enabled, cancel]);
  useEffect(() => cleanup, [cleanup]);

  const advance = useCallback(() => {
    const drag = dragRef.current;
    const scroller = scrollerRef.current;
    if (!drag || !scroller || !moved(drag)) return false;
    const { scrollDelta } = headerAtPointer(scroller, drag);
    const before = scroller.scrollLeft;
    scroller.scrollLeft += scrollDelta;
    const { target } = headerAtPointer(scroller, drag);
    setPreview((previous) =>
      previous?.id === drag.id && previous.target?.id === target?.id && previous.target?.side === target?.side
        ? previous
        : { id: drag.id, target },
    );
    return before !== scroller.scrollLeft;
  }, [scrollerRef]);
  const schedule = useCallback(() => {
    if (frameRef.current !== undefined) return;
    const tick = () => {
      frameRef.current = undefined;
      if (advance()) frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
  }, [advance]);

  const finish = () => {
    const drag = dragRef.current;
    const scroller = scrollerRef.current;
    if (!drag) return;
    const target = scroller && moved(drag) ? headerAtPointer(scroller, drag).target : null;
    cancel();
    if (!enabled || JSON.stringify(drag.ids) !== idsKey || !target || target.id === drag.id) return;
    const next = drag.ids.filter((id) => id !== drag.id);
    const index = next.indexOf(target.id);
    if (index < 0) return;
    next.splice(index + (target.side === 'after' ? 1 : 0), 0, drag.id);
    if (JSON.stringify(next) !== idsKey) commitRef.current(next);
  };

  const getDragHandlers = (id: string) => ({
    onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
      if (!enabled || !event.isPrimary || event.button !== 0 || dragRef.current) return;
      event.preventDefault();
      event.currentTarget.focus({ preventScroll: true });
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = {
        id,
        ids: columns.map((column) => column.id),
        element: event.currentTarget,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        x: event.clientX,
        y: event.clientY,
      };
    },
    onPointerMove: (event: PointerEvent<HTMLButtonElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      drag.x = event.clientX;
      drag.y = event.clientY;
      schedule();
    },
    onPointerUp: (event: PointerEvent<HTMLButtonElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      drag.x = event.clientX;
      drag.y = event.clientY;
      finish();
    },
    onPointerCancel: cancel,
    onLostPointerCapture: cancel,
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancel();
      }
    },
  });
  return { preview, getDragHandlers };
}
